import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Store } from 'lucide-react';
import { EditBusinessForm } from './EditBusinessForm';
import { navigate, safeFetch } from './App';
import { cacheSafeFetch } from './cacheSafeFetch';
import { CreateStoreModal } from './modals/CreateStoreModal';
import { LinkOrCreateStoreModal } from './modals/LinkOrCreateStoreModal';
import RegisterBusinessForm from './RegisterBusinessForm';
import { WelcomePopup } from './components/WelcomePopup';
import {
    BusinessHeader,
    BusinessMedia,
    BusinessDetails,
    BusinessContact,
    BusinessFooterActions,
    BusinessOwnerMenu,
    BusinessLocationModal,
    BusinessReviewsModal,
    BusinessNotificationModal,
    BusinessNotificationExplanationModal,
    BusinessPushNotificationModal,
} from './components/business';
import { ShareMenu } from './components/ui/ShareMenu';
import { ConfirmDialog } from './components/ui/ConfirmDialog';

const API_BASE = 'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default';

/**
 * Detect whether a string is a valid http(s) URL.
 */
function isValidHttpUrl(string) {
    try {
        const url = new URL(string);
        return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
        return false;
    }
}

/**
 * Turn URLs in a free-text string into clickable anchors.
 */
function linkifyText(text) {
    if (!text || typeof text !== 'string') return '';
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.split(urlRegex).map((part, i) =>
        urlRegex.test(part) ? (
            <a
                key={i}
                href={part}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-700 underline hover:text-brand-800"
            >
                {part}
            </a>
        ) : (
            <span key={i}>{part}</span>
        ),
    );
}

/**
 * Business — public-facing entity page (also used for events and barters).
 * Owns top-level data + visibility flags for each modal; the heavy UI lives
 * in the smaller components under `components/business/`.
 */
export const Business = ({ chatId, onLoaded }) => {
    const [businessId, setBusinessId] = useState(() => new URLSearchParams(window.location.search).get('entity'));

    useEffect(() => {
        const handle = () => setBusinessId(new URLSearchParams(window.location.search).get('entity'));
        window.addEventListener('popstate', handle);
        return () => window.removeEventListener('popstate', handle);
    }, []);

    // Core data
    const [business, setBusiness] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [allCategories, setAllCategories] = useState([]);
    const [allZones, setAllZones] = useState([]);
    const [pendingZones, setPendingZones] = useState([]);
    const [userStores, setUserStores] = useState([]);

    // Reviews
    const [reviews, setReviews] = useState([]);
    const [loadingReviews, setLoadingReviews] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [postingComment, setPostingComment] = useState(false);

    // Subscription state
    const [subscribedToUpdates, setSubscribedToUpdates] = useState(false);
    const [localNotifToggle, setLocalNotifToggle] = useState(false);

    // UI visibility flags
    const [isEditing, setIsEditing] = useState(false);
    const [showReviews, setShowReviews] = useState(false);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [showShareOptions, setShowShareOptions] = useState(false);
    const [showLocation, setShowLocation] = useState(false);
    const [showNotificationPanel, setShowNotificationPanel] = useState(false);
    const [showNotificationExplanation, setShowNotificationExplanation] = useState(false);
    const [showPushNotificationPanel, setShowPushNotificationPanel] = useState(false);
    const [showBusinessMenu, setShowBusinessMenu] = useState(false);
    const [showCreateStoreModal, setShowCreateStoreModal] = useState(false);
    const [showLinkOrCreateModal, setShowLinkOrCreateModal] = useState(false);
    const [showWelcomePopup, setShowWelcomePopup] = useState(false);
    const [showCreateEventModal, setShowCreateEventModal] = useState(false);

    const menuRef = useRef(null);
    const toggleButtonRef = useRef(null);

    const isGuest = chatId === 'guest';

    // Fetch business
    useEffect(() => {
        if (!businessId) return;
        window.scrollTo({ top: 0, behavior: 'instant' });

        const urlParams = new URLSearchParams(window.location.search);
        const shouldShowWelcome = urlParams.get('welcome') === 'true';
        const welcomeKey = `business-welcome-shown-${businessId}`;
        if (shouldShowWelcome && !localStorage.getItem(welcomeKey)) {
            setShowWelcomePopup(true);
            localStorage.setItem(welcomeKey, 'true');
            urlParams.delete('welcome');
            const newUrl = urlParams.toString() ? `${window.location.pathname}?${urlParams.toString()}` : window.location.pathname;
            window.history.replaceState({}, '', newUrl);
        }

        let mounted = true;
        setLoading(true);
        setError('');
        (async () => {
            try {
                const res = await safeFetch(`${API_BASE}/business-info?business_id=${businessId}&chat_id=${chatId}`, chatId);
                const data = await res.json();
                if (!mounted) return;
                setBusiness(data);
                onLoaded?.(data);
                if (Array.isArray(data.images_urls)) {
                    data.images_urls.forEach((url) => { const img = new Image(); img.src = url; });
                }
            } catch (err) {
                if (!mounted) return;
                console.error('❌ Failed to fetch business info:', err);
                setError('שגיאה בטעינת פרטי העסק');
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [businessId, chatId]);

    // Fetch user-owned stores (so we know whether to offer linking)
    useEffect(() => {
        if (!chatId || isGuest) return;
        (async () => {
            try {
                const entitiesRes = await safeFetch(`${API_BASE}/user-entities?chat_id=${chatId}`, chatId);
                const entitiesData = await entitiesRes.json();
                const adminStoreIds = entitiesData['admin_stores'] || [];
                if (adminStoreIds.length > 0) {
                    const storeApiUrl = process.env.REACT_APP_STORE_API_URL || 'https://v3ugnvc3qk.execute-api.us-west-2.amazonaws.com/prod';
                    const storesData = await Promise.all(
                        adminStoreIds.map((storeId) =>
                            fetch(`${storeApiUrl}/get-store-by-id?store_id=${storeId}`)
                                .then((r) => r.json())
                                .catch(() => null),
                        ),
                    );
                    setUserStores(storesData.filter(Boolean));
                }
            } catch (err) {
                console.error('❌ Failed to fetch user stores:', err);
            }
        })();
    }, [chatId, isGuest]);

    // Close owner menu on outside click
    useEffect(() => {
        if (!showBusinessMenu) return;
        const onMouseDown = (e) => {
            if (
                menuRef.current && !menuRef.current.contains(e.target) &&
                toggleButtonRef.current && !toggleButtonRef.current.contains(e.target)
            ) {
                setShowBusinessMenu(false);
            }
        };
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, [showBusinessMenu]);

    // Lazy-load categories/zones only when entering edit mode
    useEffect(() => {
        if (!isEditing) return;
        (async () => {
            try {
                const [allRes] = await Promise.all([
                    cacheSafeFetch(`${API_BASE}/zones-and-categories`, chatId, { method: 'GET' }, { cacheKey: `zones-and-categories-${chatId}` }),
                    safeFetch(`${API_BASE}/user-info?chat_id=${chatId}`, chatId),
                ]);
                const allData = await allRes.json();
                setAllCategories(allData.categories || []);
                setAllZones(allData.zones || []);
                setPendingZones(allData.pending_zones || []);
            } catch (err) {
                console.error('❌ Failed to load categories for edit:', err);
            }
        })();
    }, [isEditing, chatId]);

    // Keep the notification-panel toggle in sync with the global subscription state
    useEffect(() => { setLocalNotifToggle(subscribedToUpdates); }, [subscribedToUpdates]);

    // Handlers
    const toggleBusinessUpdates = async () => {
        const next = !subscribedToUpdates;
        setSubscribedToUpdates(next);
        setShowNotificationExplanation(next);
        try {
            await safeFetch(`${API_BASE}/toggle-business-subscription`, chatId, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ chat_id: chatId, business_id: businessId, subscribe: next }),
            });
        } catch (err) {
            console.error('❌ Failed to update subscription:', err);
            setSubscribedToUpdates(!next);
        }
    };

    const handleSubmitComment = async () => {
        if (isGuest) { alert('שירות זה זמין רק למשתמשים מחוברים'); return; }
        if (!newComment.trim()) return;
        setPostingComment(true);
        try {
            const res = await safeFetch(`${API_BASE}/add-business-review`, chatId, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ business_id: businessId, chat_id: chatId, comment: newComment.trim() }),
            });
            if (!res.ok) throw new Error('Failed to post comment');
            setReviews((prev) => [...prev, { review: newComment.trim(), user_id: chatId }]);
            setNewComment('');
        } catch (err) {
            console.error('❌ Failed to post comment:', err);
            alert('שגיאה בשליחת התגובה');
        } finally {
            setPostingComment(false);
        }
    };

    const handleToggleReviews = async () => {
        if (!showReviews) {
            setLoadingReviews(true);
            try {
                const res = await safeFetch(`${API_BASE}/business-reviews?business_id=${businessId}&chat_id=${chatId}`, chatId);
                const data = await res.json();
                const arr = Array.isArray(data)
                    ? data
                    : Array.isArray(data.business_reviews) ? data.business_reviews : [];
                setReviews(arr);
            } catch (err) {
                console.error('❌ Failed to fetch business reviews:', err);
            } finally {
                setLoadingReviews(false);
            }
        }
        setShowReviews(!showReviews);
    };

    const handleDelete = async () => {
        try {
            if (business.store_id) {
                try {
                    const storeApiUrl = process.env.REACT_APP_STORE_API_URL || 'https://v3ugnvc3qk.execute-api.us-west-2.amazonaws.com/prod';
                    await fetch(`${storeApiUrl}/admin/store/${business.store_id}`, { method: 'DELETE' });
                } catch (storeErr) {
                    console.error('⚠️ Error deleting linked store:', storeErr);
                }
            }
            const res = await safeFetch(`${API_BASE}/delete-business`, chatId, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ business_id: business.business_id, chat_id: chatId }),
            });
            if (!res.ok) throw new Error('Failed to delete');
            navigate('my-businesses', `chat_id=${chatId}&t=${Date.now()}`);
        } catch (err) {
            console.error(`❌ Failed to delete:`, err);
            alert(`שגיאה במחיקת ה${getEntityLabel()}`);
        }
    };

    const handleSaveEdit = async (updatedBusiness) => {
        try {
            const imageFilenames = updatedBusiness.images_urls
                .map((url) => {
                    try {
                        const decoded = decodeURIComponent(url);
                        const match = decoded.match(/\/([^\/\?]+)(?:\?|$)/);
                        return match ? match[1] : null;
                    } catch { return null; }
                })
                .filter(Boolean);

            const decoded = decodeURIComponent(updatedBusiness.cover_image);
            const match = decoded.match(/\/([^\/\?]+)(?:\?|$)/);
            updatedBusiness.cover_image = match ? match[1] : null;

            const backendPayload = {
                ...updatedBusiness,
                business_images: JSON.stringify(imageFilenames),
                business_category: JSON.stringify(updatedBusiness.business_category),
            };
            delete backendPayload.images_urls;

            const res = await safeFetch(`${API_BASE}/update-business`, chatId, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ updatedBusiness: backendPayload, chat_id: chatId }),
            });
            if (!res.ok) throw new Error('Failed to update business');

            const biz = await safeFetch(`${API_BASE}/business-info?business_id=${businessId}&chat_id=${chatId}`, chatId);
            setBusiness(await biz.json());
            setIsEditing(false);
        } catch (err) {
            console.error('❌ Error updating business:', err);
            alert('שגיאה בשמירת השינויים');
        }
    };

    // Derived values
    if (loading) return null;
    if (error) return <p className="text-danger-600 text-center">{error}</p>;
    if (!business) return null;

    const isOwner = business.business_owner;
    const firstCategory = business.business_category?.[0] || '';
    const firstZoneObj = business.zones?.[0];
    const firstZone = firstZoneObj
        ? (typeof firstZoneObj === 'string' ? firstZoneObj : (firstZoneObj.zone_display_name || firstZoneObj.zone_id))
        : '';

    const getEntityLabel = () => {
        const type = business.type;
        if (type === 'event') return 'מפגש';
        if (type === 'barter') return 'ברטר';
        return 'עסק';
    };

    const entityLabel = getEntityLabel();

    const handleOpenDirections = business.location ? () => {
        try {
            const parsed = JSON.parse(business.location.replaceAll("'", '"'));
            if (parsed?.lat && parsed?.lng) {
                window.open(`https://www.google.com/maps/search/?api=1&query=${parsed.lat},${parsed.lng}`, '_blank');
            }
        } catch { /* parse failure — ignore */ }
    } : null;

    return (
        <div className="min-h-screen bg-surface-base pb-20 md:pb-0" dir="rtl">
            {isEditing ? (
                <EditBusinessForm
                    allCategories={allCategories}
                    allZones={allZones}
                    pendingZones={pendingZones}
                    business={business}
                    chatId={chatId}
                    onCancel={() => setIsEditing(false)}
                    onSave={handleSaveEdit}
                />
            ) : (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                    className="max-w-4xl mx-auto bg-surface shadow-elevated md:my-8 md:rounded-2xl overflow-hidden relative"
                >
                    {/* Back button */}
                    <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: 0.05 }}
                        className="bg-surface border-b border-line-subtle flex items-center justify-between px-4 py-2"
                    >
                        <motion.button
                            whileHover={{ x: -4 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => window.history.back()}
                            className="flex items-center gap-2 px-4 py-2 text-ink-muted hover:text-ink transition-colors rounded-lg hover:bg-surface-muted"
                            aria-label="חזור"
                        >
                            <ArrowLeft className="h-5 w-5" strokeWidth={2} />
                            <span className="text-sm font-semibold">חזור</span>
                        </motion.button>
                    </motion.div>

                    <BusinessHeader
                        businessName={business.business_name}
                        category={firstCategory}
                        location={firstZone}
                        isOwner={isOwner}
                        onMenuToggle={() => setShowBusinessMenu((p) => !p)}
                        onCreateStore={() => {
                            if (userStores.length > 0) setShowLinkOrCreateModal(true);
                            else setShowCreateStoreModal(true);
                        }}
                        onCreateEvent={() => setShowCreateEventModal(true)}
                        onGoToStore={() => navigate('store', `slug=${business.store_slug}`)}
                        hasStore={business.has_store}
                        websiteUrl={isValidHttpUrl(business.business_url) ? business.business_url : null}
                        menuIcon={<span className="text-2xl" role="img" aria-label="menu">⋮</span>}
                        toggleButtonRef={toggleButtonRef}
                        businessType={business.type}
                    />

                    <BusinessOwnerMenu
                        isOpen={showBusinessMenu}
                        entityLabel={entityLabel}
                        approved={business.approved}
                        menuRef={menuRef}
                        onEdit={() => { setIsEditing(true); setShowBusinessMenu(false); }}
                        onDelete={() => { setShowConfirmDelete(true); setShowBusinessMenu(false); }}
                    />

                    <BusinessMedia
                        coverImage={business.cover_image}
                        images={business.images_urls}
                    />

                    <BusinessDetails
                        description={business.business_description}
                        categories={business.business_category}
                        zones={business.zones}
                        eventTime={business.event_time}
                        linkifyText={linkifyText}
                        founderName={business.founder_name}
                        founderAbout={business.founder_about}
                        founderImageUrl={business.founder_image_url}
                        type={business.type}
                        activeDays={business.active_days}
                        address={business.address}
                    />

                    <BusinessContact
                        phoneNumber={business.business_number}
                        whatsappAvailable={business.number_available_on_whatsapp}
                        websiteUrl={isValidHttpUrl(business.business_url) ? business.business_url : null}
                        channelUrl={isValidHttpUrl(business.channel) ? business.channel : null}
                        location={business.location}
                        onLocationClick={business.location ? () => setShowLocation(true) : null}
                        onNotificationClick={() => setShowNotificationPanel(true)}
                        onReviewsClick={handleToggleReviews}
                        onShareClick={() => setShowShareOptions(true)}
                    />

                    {/* Linked-business CTA for events */}
                    {business.type === 'event' && business.parent_business_id && (
                        <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3 }}
                            className="px-6 pb-4"
                        >
                            <button
                                type="button"
                                onClick={() => navigate('business', `entity=${business.parent_business_id}`)}
                                className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-secondary-500 hover:bg-secondary-600 text-ink-inverse rounded-xl font-bold shadow-card hover:shadow-elevated transition-[background-color,box-shadow,transform] duration-200 ease-smooth hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2"
                            >
                                <Store className="w-5 h-5" strokeWidth={2} />
                                <span>עבור לעסק המארח</span>
                            </button>
                        </motion.div>
                    )}

                    <BusinessFooterActions
                        phoneNumber={business.business_number}
                        whatsappAvailable={business.number_available_on_whatsapp}
                        onCall={() => window.location.href = `tel:${business.business_number?.replace(/[^0-9+]/g, '')}`}
                        onMessage={() => window.open(`https://wa.me/${business.business_number?.replace(/[^0-9]/g, '')}`, '_blank')}
                        onDirections={handleOpenDirections}
                        onShare={() => setShowShareOptions(true)}
                        showOnDesktop={false}
                    />
                </motion.div>
            )}

            {/* Extracted modals */}
            <BusinessLocationModal
                isOpen={showLocation}
                locationString={business.location}
                onClose={() => setShowLocation(false)}
            />

            <BusinessReviewsModal
                isOpen={showReviews}
                onClose={handleToggleReviews}
                reviews={reviews}
                loading={loadingReviews}
                isGuest={isGuest}
                newComment={newComment}
                onChangeComment={setNewComment}
                onSubmitComment={handleSubmitComment}
                posting={postingComment}
            />

            <BusinessNotificationModal
                isOpen={showNotificationPanel}
                onClose={() => setShowNotificationPanel(false)}
                isGuest={isGuest}
                subscribed={localNotifToggle}
                onToggleSubscribed={() => {
                    setLocalNotifToggle((v) => !v);
                    toggleBusinessUpdates();
                }}
            />

            <BusinessNotificationExplanationModal
                isOpen={showNotificationExplanation}
                onClose={() => setShowNotificationExplanation(false)}
                businessId={business.business_id}
                chatId={chatId}
            />

            <BusinessPushNotificationModal
                isOpen={showPushNotificationPanel}
                onClose={() => setShowPushNotificationPanel(false)}
                business={business}
                chatId={chatId}
            />

            <ShareMenu
                isOpen={showShareOptions}
                onClose={() => setShowShareOptions(false)}
                url={`https://hamekomit.com/?view=business&entity=${business.business_id}`}
                title={`${business.business_name} — המקומית`}
                heading={`שתפו את ${business.business_name}`}
            />

            <ConfirmDialog
                isOpen={showConfirmDelete}
                onClose={() => setShowConfirmDelete(false)}
                onConfirm={handleDelete}
                title={`למחוק את ה${entityLabel}?`}
                description="פעולה זו אינה הפיכה. כל הנתונים הקשורים יימחקו."
                confirmLabel="כן, מחק"
                confirmVariant="danger"
            />

            {showLinkOrCreateModal && (
                <LinkOrCreateStoreModal
                    onClose={() => setShowLinkOrCreateModal(false)}
                    onCreateNew={() => {
                        setShowLinkOrCreateModal(false);
                        setShowCreateStoreModal(true);
                    }}
                    onLinkExisting={(linked) => {
                        setBusiness((prev) => ({
                            ...prev,
                            has_store: true,
                            store_id: linked.store_id,
                            store_slug: linked.store_slug,
                        }));
                    }}
                    userStores={userStores}
                    businessId={businessId}
                    userId={chatId}
                />
            )}

            {showCreateStoreModal && (
                <CreateStoreModal
                    onClose={() => setShowCreateStoreModal(false)}
                    userId={chatId}
                    businessId={businessId}
                    onStoreCreated={(storeData) => {
                        setBusiness((prev) => ({
                            ...prev,
                            has_store: storeData.has_store,
                            store_id: storeData.store_id,
                            store_slug: storeData.store_slug,
                        }));
                    }}
                />
            )}

            {showWelcomePopup && business && business.type !== 'barter' && (
                <WelcomePopup
                    type="business"
                    name={business.business_name}
                    onClose={() => setShowWelcomePopup(false)}
                />
            )}

            {showCreateEventModal && (
                <RegisterBusinessForm
                    chatId={chatId}
                    initialType="event"
                    initialZone={firstZone}
                    linkedBusinessId={businessId}
                    onClose={() => setShowCreateEventModal(false)}
                />
            )}
        </div>
    );
};

export default Business;
